import React, { useState, useEffect, useMemo } from 'react';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import PTOStatsCards from './components/PTOStatsCards';
import PTOFilterPanel from './components/PTOFilterPanel';
import PTOBalanceTable from './components/PTOBalanceTable';
import PTORequestsTable from './components/PTORequestsTable';
import AddPTORequestModal from './components/AddPTORequestModal';
import PTOCalendar from './components/PTOCalendar';
import PTONotificationsPanel from './components/PTONotificationsPanel';
import { supabase } from '../../lib/supabase';
import { employeesService, departmentsService } from '../../services/supabaseService';

// ── Supabase helpers ──────────────────────────────────────────────────────────
const toCamel = (str) => str?.replace(/_([a-z])/g, (_, c) => c?.toUpperCase());
const convertKeys = (obj) => {
  if (Array.isArray(obj)) return obj?.map(convertKeys);
  if (obj && typeof obj === 'object') {
    return Object.keys(obj)?.reduce((acc, k) => {
      acc[toCamel(k)] = convertKeys(obj?.[k]);
      return acc;
    }, {});
  }
  return obj;
};

const ptoService = {
  async getRequests() {
    const { data, error } = await supabase?.from('pto_requests')?.select('*')?.order('created_at', { ascending: false });
    if (error) throw error;
    return convertKeys(data ?? []);
  },

  async getBalances(year = new Date()?.getFullYear()) {
    const { data, error } = await supabase?.from('pto_balances')?.select('*')?.eq('year', year);
    if (error) throw error;
    return convertKeys(data ?? []);
  },

  async createRequest(payload) {
    const { data, error } = await supabase?.from('pto_requests')?.insert([{
        employee_id: payload?.employeeId,
        pto_type: payload?.ptoType,
        start_date: payload?.startDate,
        end_date: payload?.endDate,
        days_requested: payload?.daysRequested,
        reason: payload?.reason,
        coverage_arrangement: payload?.coverageArrangement,
        status: 'Pending',
      }])?.select()?.single();
    if (error) throw error;
    return convertKeys(data);
  },

  async approveRequest(id) {
    const { data, error } = await supabase?.from('pto_requests')?.update({
        status: 'Approved',
        approved_by: 'HR Manager',
        approved_date: new Date()?.toISOString()?.split('T')?.[0],
      })?.eq('id', id)?.select()?.single();
    if (error) throw error;
    return convertKeys(data);
  },

  async denyRequest(id, denialReason) {
    const { data, error } = await supabase?.from('pto_requests')?.update({ status: 'Denied', denial_reason: denialReason })?.eq('id', id)?.select()?.single();
    if (error) throw error;
    return convertKeys(data);
  },

  async bulkApprove(ids) {
    const { data, error } = await supabase?.from('pto_requests')?.update({
        status: 'Approved',
        approved_by: 'HR Manager',
        approved_date: new Date()?.toISOString()?.split('T')?.[0],
      })?.in('id', ids)?.select();
    if (error) throw error;
    return convertKeys(data ?? []);
  },

  async deleteRequest(id) {
    const { error } = await supabase?.from('pto_requests')?.delete()?.eq('id', id);
    if (error) throw error;
  },

  async adjustBalance(balanceId, employeeId, adjustmentHours, reason) {
    // Update the hours_adjusted field by incrementing it
    const { data: current, error: fetchErr } = await supabase
      ?.from('pto_balances')
      ?.select('hours_adjusted')
      ?.eq('id', balanceId)
      ?.single();
    if (fetchErr) throw fetchErr;

    const newAdjusted = parseFloat(current?.hours_adjusted ?? 0) + adjustmentHours;

    const { data, error } = await supabase
      ?.from('pto_balances')
      ?.update({ hours_adjusted: newAdjusted })
      ?.eq('id', balanceId)
      ?.select()
      ?.single();
    if (error) throw error;

    // Log the adjustment
    await supabase?.from('pto_balance_adjustments')?.insert([{
      balance_id: balanceId,
      employee_id: employeeId,
      adjusted_by: 'HR Manager',
      adjustment_hours: adjustmentHours,
      reason,
    }]);

    return convertKeys(data);
  },
};

// ── Component ─────────────────────────────────────────────────────────────────
const PTOManagement = () => {
  const [requests, setRequests] = useState([]);
  const [balances, setBalances] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [activeTab, setActiveTab] = useState('requests');
  const [filters, setFilters] = useState({
    search: '',
    ptoType: 'all',
    status: 'all',
    department: 'all',
    dateFrom: '',
    dateTo: '',
  });

  const breadcrumbItems = [
    { label: 'Home', path: '/' },
    { label: 'PTO Management', path: '/pto-management', active: true },
  ];

  // ── Load data ──────────────────────────────────────────────────────────────
  const loadData = async () => {
    try {
      setLoading(true);
      setError('');
      const [reqs, bals, emps, depts] = await Promise.all([
        ptoService?.getRequests(),
        ptoService?.getBalances(),
        employeesService?.getAll(),
        departmentsService?.getAll(),
      ]);
      setRequests(reqs);
      setBalances(bals);
      setEmployees(emps);
      setDepartments(depts?.map((d) => d?.name) ?? []);
    } catch (err) {
      console.error('Error loading PTO data:', err);
      setError('Failed to load PTO data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  // ── Stats ──────────────────────────────────────────────────────────────────
  const stats = useMemo(() => {
    const now = new Date();
    const thisMonth = requests?.filter((r) => {
      const d = new Date(r?.createdAt);
      return d?.getMonth() === now?.getMonth() && d?.getFullYear() === now?.getFullYear();
    });
    const approved = thisMonth?.filter((r) => r?.status === 'Approved');
    const pending = requests?.filter((r) => r?.status === 'Pending');
    const totalDays = requests?.reduce((sum, r) => sum + parseFloat(r?.daysRequested ?? 0), 0);
    return {
      totalRequests: requests?.length ?? 0,
      pendingCount: pending?.length ?? 0,
      approvedThisMonth: approved?.length ?? 0,
      avgDays: requests?.length ? totalDays / requests?.length : 0,
    };
  }, [requests]);

  // ── Filtered data ──────────────────────────────────────────────────────────
  const filteredRequests = useMemo(() => {
    let result = requests ?? [];

    if (filters?.search) {
      const q = filters?.search?.toLowerCase();
      const matchingEmpIds = employees
        ?.filter((e) => e?.name?.toLowerCase()?.includes(q) || e?.employeeId?.toLowerCase()?.includes(q))
        ?.map((e) => e?.id);
      result = result?.filter((r) => matchingEmpIds?.includes(r?.employeeId));
    }

    if (filters?.ptoType !== 'all') {
      result = result?.filter((r) => r?.ptoType === filters?.ptoType);
    }

    if (filters?.status !== 'all') {
      result = result?.filter((r) => r?.status === filters?.status);
    }

    if (filters?.department !== 'all') {
      const deptEmpIds = employees
        ?.filter((e) => e?.department === filters?.department)
        ?.map((e) => e?.id);
      result = result?.filter((r) => deptEmpIds?.includes(r?.employeeId));
    }

    if (filters?.dateFrom) {
      result = result?.filter((r) => r?.startDate >= filters?.dateFrom);
    }

    if (filters?.dateTo) {
      result = result?.filter((r) => r?.startDate <= filters?.dateTo);
    }

    return result;
  }, [requests, employees, filters]);

  const filteredEmployees = useMemo(() => {
    if (!filters?.search && filters?.department === 'all') return employees;
    return employees?.filter((e) => {
      const matchSearch = !filters?.search ||
        e?.name?.toLowerCase()?.includes(filters?.search?.toLowerCase()) ||
        e?.employeeId?.toLowerCase()?.includes(filters?.search?.toLowerCase());
      const matchDept = filters?.department === 'all' || e?.department === filters?.department;
      return matchSearch && matchDept;
    });
  }, [employees, filters]);

  // ── Actions ────────────────────────────────────────────────────────────────
  const handleApprove = async (id) => {
    try {
      const updated = await ptoService?.approveRequest(id);
      setRequests((prev) => prev?.map((r) => r?.id === id ? updated : r));
    } catch (err) {
      console.error('Approve error:', err);
    }
  };

  const handleDeny = async (id, reason) => {
    try {
      const updated = await ptoService?.denyRequest(id, reason);
      setRequests((prev) => prev?.map((r) => r?.id === id ? updated : r));
    } catch (err) {
      console.error('Deny error:', err);
    }
  };

  const handleBulkApprove = async (ids) => {
    try {
      const updated = await ptoService?.bulkApprove(ids);
      const updatedMap = Object.fromEntries(updated?.map((r) => [r?.id, r]));
      setRequests((prev) => prev?.map((r) => updatedMap?.[r?.id] ?? r));
    } catch (err) {
      console.error('Bulk approve error:', err);
    }
  };

  const handleAddRequest = async (payload) => {
    const newReq = await ptoService?.createRequest(payload);
    setRequests((prev) => [newReq, ...prev]);
  };

  const handleDeleteRequest = async (id) => {
    if (!window.confirm('Are you sure you want to delete this PTO request? This action cannot be undone.')) return;
    try {
      await ptoService?.deleteRequest(id);
      setRequests((prev) => prev?.filter((r) => r?.id !== id));
    } catch (err) {
      console.error('Delete error:', err);
    }
  };

  const handleEditBalance = async ({ balanceId, employeeId, hours, reason }) => {
    try {
      const updated = await ptoService?.adjustBalance(balanceId, employeeId, hours, reason);
      setBalances((prev) => prev?.map((b) => b?.id === balanceId ? updated : b));
    } catch (err) {
      console.error('Balance edit error:', err);
      throw err;
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-16">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 py-6">
          <Breadcrumb items={breadcrumbItems} />

          {/* Page Header */}
          <div className="flex items-center justify-between mb-6 mt-2">
            <div>
              <h1 className="text-2xl font-heading font-bold text-foreground">PTO Management</h1>
              <p className="text-sm font-body text-muted-foreground mt-0.5">
                Track and manage employee paid time off requests and balances
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={loadData}
                className="p-2 rounded-lg hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
                title="Refresh"
              >
                <Icon name="RefreshCw" size={16} />
              </button>
              <Button
                variant="default"
                iconName="CalendarPlus"
                onClick={() => setShowAddModal(true)}
              >
                New Request
              </Button>
            </div>
          </div>

          {/* Error Banner */}
          {error && (
            <div className="flex items-center gap-2 p-4 mb-4 bg-destructive/10 text-destructive rounded-xl text-sm">
              <Icon name="AlertCircle" size={16} />
              {error}
              <button onClick={loadData} className="ml-auto underline text-xs">Retry</button>
            </div>
          )}

          {/* Stats */}
          <PTOStatsCards stats={stats} loading={loading} />

          {/* Main Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6">
            {/* Filter Sidebar */}
            <aside>
              <PTOFilterPanel
                filters={filters}
                onChange={setFilters}
                departments={departments}
              />
            </aside>

            {/* Content Area */}
            <div className="space-y-6">
              {/* Tabs */}
              <div className="flex items-center gap-1 bg-muted/40 rounded-xl p-1 w-fit">
                {[
                  { key: 'requests', label: 'Requests', icon: 'ListChecks' },
                  { key: 'calendar', label: 'Calendar', icon: 'CalendarDays' },
                  { key: 'balances', label: 'Balances', icon: 'Wallet' },
                  { key: 'notifications', label: 'Notifications', icon: 'Bell' },
                ]?.map((tab) => (
                  <button
                    key={tab?.key}
                    onClick={() => setActiveTab(tab?.key)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-body font-medium transition-colors ${
                      activeTab === tab?.key
                        ? 'bg-card text-foreground elevation-sm'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    <Icon name={tab?.icon} size={15} />
                    {tab?.label}
                    {tab?.key === 'requests' && stats?.pendingCount > 0 && (
                      <span className="bg-warning text-warning-foreground text-xs px-1.5 py-0.5 rounded-full">
                        {stats?.pendingCount}
                      </span>
                    )}
                    {tab?.key === 'notifications' && stats?.pendingCount > 0 && (
                      <span className="bg-destructive text-destructive-foreground text-xs px-1.5 py-0.5 rounded-full">
                        {stats?.pendingCount}
                      </span>
                    )}
                  </button>
                ))}
              </div>

              {/* Tab Content */}
              {activeTab === 'requests' && (
                <PTORequestsTable
                  requests={filteredRequests}
                  employees={employees}
                  onApprove={handleApprove}
                  onDeny={handleDeny}
                  onBulkApprove={handleBulkApprove}
                  onDelete={handleDeleteRequest}
                  loading={loading}
                />
              )}

              {activeTab === 'calendar' && (
                <PTOCalendar
                  requests={requests}
                  employees={employees}
                />
              )}

              {activeTab === 'balances' && (
                <PTOBalanceTable
                  employees={filteredEmployees}
                  balances={balances}
                  loading={loading}
                  onEditBalance={handleEditBalance}
                />
              )}

              {activeTab === 'notifications' && (
                <PTONotificationsPanel
                  requests={requests}
                  employees={employees}
                  balances={balances}
                  onApprove={handleApprove}
                  onDeny={handleDeny}
                />
              )}
            </div>
          </div>
        </div>
      </main>
      {/* Add Request Modal */}
      {showAddModal && (
        <AddPTORequestModal
          employees={employees}
          onClose={() => setShowAddModal(false)}
          onSubmit={handleAddRequest}
        />
      )}
    </div>
  );
};

export default PTOManagement;
